<template>
  <div class="cms-activities">
    <cms-page-header
      :title="$t('cms.activities.title')"
      :create-text="$t('cms.activities.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.activities.category')">
        <select v-model="selectedCategory" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option
            v-for="(label, key) in categories"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.activities.status')">
        <select v-model="selectedStatus" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option
            v-for="(label, key) in statusOptions"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.activities.modality')">
        <select v-model="selectedModality" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option
            v-for="(label, key) in modalityOptions"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.activities.difficulty')">
        <select v-model="selectedDifficulty" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option
            v-for="(label, key) in difficultyOptions"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.activities.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button
          class="cms-btn cms-btn-secondary"
          type="button"
          @click="loadActivities"
          :disabled="isLoading"
        >
          <v-icon icon="refresh" size="sm" class="icon-accent-primary" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-activities-error">
      <p>{{ errorMessage }}</p>
      <button class="cms-btn-link" type="button" @click="loadActivities">
        {{ $t('general.retry') }}
      </button>
    </div>

    <div v-if="isLoading" class="cms-activities-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
      <p class="loading-subtitle">Fetching your activities...</p>
    </div>

    <div v-else-if="filteredActivities.length === 0" class="cms-empty-state-wrapper">
      <cms-empty-state
        :message="$t('cms.activities.emptyStateTitle')"
        :hint="$t('cms.activities.emptyStateHint')"
      />
      <div class="empty-state-actions">
        <button 
          class="cms-btn cms-btn-primary"
          @click="navigateToCreate"
        >
          <v-icon icon="plus" size="sm" class="icon-accent-primary" />
          Create Your First Activity
        </button>
      </div>
    </div>

    <cms-grid v-else min-width="320px">
      <cms-card
        v-for="activity in filteredActivities"
        :key="activity.id"
        clickable
        class="cms-activity-card"
        @click="navigateToEdit(activity.id)"
      >
        <template #status>
          <cms-status-badge :status="activity.status">
            {{ statusOptions[activity.status] || activity.status }}
          </cms-status-badge>
        </template>

        <template #actions>
          <cms-icon-button
            :icon="activity.isPublic ? 'eye-blocked' : 'eye'"
            :variant="activity.isPublic ? 'secondary' : 'primary'"
            :disabled="publishingId === activity.id"
            :tooltip="activity.isPublic ? 'Unpublish' : 'Publish'"
            @click.stop="handleTogglePublish(activity.id)"
          />
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEdit(activity.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === activity.id"
            :tooltip="$t('general.delete')"
            @click.stop="handleDelete(activity.id)"
          />
        </template>

        <div v-if="activity.featuredImage?.storageUrl" class="cms-activity-preview" :style="{ backgroundImage: `url(${activity.featuredImage.storageUrl})` }" />

        <div class="cms-activity-header">
          <h3 class="cms-activity-title">{{ activity.title }}</h3>
          <div class="cms-activity-badges">
            <span
              v-if="activity.modality"
              class="cms-activity-modality"
              :class="getModalityClass(activity.modality)"
            >
              {{ getModalityLabel(activity.modality) }}
            </span>
            <span v-if="activity.isFeatured" class="cms-activity-featured">{{ $t('general.shared.featured') }}</span>
          </div>
        </div>

        <template #footer>
          <div class="cms-card-footer">
            <span>{{ activity.updatedAtLabel }}</span>
          </div>
        </template>
      </cms-card>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      :per-page-options="[9, 18, 27, 36]"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsStatusBadge,
  CmsIconButton,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';

interface CmsActivityCard {
  id: number;
  title: string;
  summary: string;
  bsName: string;
  categoryKeys: string[];
  categoryLabel: string;
  location: string;
  duration: string;
  availableLanguagesLabel: string;
  status: 'draft' | 'published';
  updatedAt: string;
  updatedAtLabel: string;
  isPublic: boolean;
  isFeatured: boolean;
  featuredImage?: {
    storageUrl?: string;
    originalUrl?: string;
  };
  modality?: string;
  base_price?: number;
}

export default defineComponent({
  name: 'TheCmsActivities',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsStatusBadge,
    CmsIconButton,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const { resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));
    const activities = ref<CmsActivityCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref<string>('');
    const publishingId = ref<number | null>(null);
    const deletingId = ref<number | null>(null);

    const selectedCategory = ref('');
    const selectedStatus = ref('');
    const selectedModality = ref('');
    const selectedDifficulty = ref('');
    const searchQuery = ref('');

    // Pagination state
    const currentPage = ref(1);
    const perPage = ref(9);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const categories = computed(() => ({
      adventure: t('cms.activities.categories.adventure'),
      cultural: t('cms.activities.categories.cultural'),
      nature: t('cms.activities.categories.nature'),
      relaxation: t('cms.activities.categories.relaxation'),
      sports: t('cms.activities.categories.sports'),
    }));

    const statusOptions = computed(() => ({
      draft: t('cms.activities.statusOptions.draft'),
      published: t('cms.activities.statusOptions.published'),
      archived: t('cms.activities.statusOptions.archived'),
    }));

    const modalityOptions = computed(() => ({
      'self-guided': t('cms.activities.modalityOptions.selfGuided'),
      group: t('cms.activities.modalityOptions.group'),
    }));

    const normalizeModalityKey = (modality: string | null | undefined): string | null => {
      if (!modality) return null;
      const key = modality.toLowerCase().replace(/_/g, '-');
      if (key === 'self-guided' || key === 'selfguided') return 'self-guided';
      if (key === 'group') return 'group';
      return key;
    };

    const getModalityLabel = (modality: string | null | undefined) => {
      const key = normalizeModalityKey(modality);
      if (!key) return '';
      return modalityOptions.value[key as keyof typeof modalityOptions.value] || modality || '';
    };

    const getModalityClass = (modality: string | null | undefined) => {
      const key = normalizeModalityKey(modality);
      return key ? `cms-activity-modality--${key}` : '';
    };

    const difficultyOptions = computed(() => ({
      easy: 'Easy',
      medium: 'Medium',
      intermediate: 'Intermediate',
      hard: 'Hard',
    }));

    const agencySlug = computed(() => authStore.agency?.slug ?? null);

    const extractListData = (response: any): any[] => {
      if (!response) return [];

      const candidates = [
        response.data?.data,
        response.data,
        response?.data?.data?.data,
        response,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }

      return [];
    };

    const formatDateTime = (value: string) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString();
    };

    const getCategoryLabel = (keys: string[]) => {
      if (!keys.length) return '';
      return keys
        .map((key) => categories.value[key as keyof typeof categories.value] || key)
        .join(', ');
    };

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item?.attributes || item;
      return (
        attributes.original_url ||
        attributes.storage_url ||
        attributes.url ||
        attributes.preview_url ||
        item.original_url ||
        item.storage_url ||
        item.url ||
        item.storageUrl ||
        ''
      );
    };

    const extractMediaItems = (activity: any, included: any[] = []) => {
      if (Array.isArray(activity?.media)) {
        return activity.media;
      }
      if (activity?.relationships?.media?.data) {
        const mediaData = Array.isArray(activity.relationships.media.data)
          ? activity.relationships.media.data
          : [activity.relationships.media.data];
        
        // Map media IDs to actual media objects from included data
        return mediaData.map((mediaRef: any) => {
          const mediaItem = included.find((item: any) => 
            item.type === 'media' && String(item.id) === String(mediaRef.id)
          );
          return mediaItem || mediaRef;
        }).filter(Boolean);
      }
      return [];
    };

    const mapActivity = (item: any, included: any[] = []): CmsActivityCard => {
      const translations = Array.isArray(item?.translations) ? item.translations : [];
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === currentLocale.value) ||
        translations[0] ||
        {};
      const bsActivity = item?.activity || {};

      const title = translation?.title || item?.attributes?.title || item?.title || bsActivity?.name || '-';
      const summary = translation?.seo_description || translation?.content || item?.attributes?.content || '';
      const bsName = bsActivity?.name || '';

      const categoryKeys = Array.isArray(item?.attributes?.categories || item?.categories)
        ? (item?.attributes?.categories || item?.categories).filter((c: string) => !!c)
        : [];

      const availableLanguages = Array.isArray(item?.attributes?.available_languages || item?.available_languages)
        ? (item?.attributes?.available_languages || item?.available_languages)
        : [];

      const locationParts = [
        item?.attributes?.general_country || item?.general_country,
        bsActivity?.origin?.name,
        bsActivity?.destination?.name,
      ].filter(Boolean);

      const location = locationParts
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(' → ');

      const duration = (item?.attributes?.duration || item?.duration) || (bsActivity?.total_days ? `${bsActivity.total_days} ${t('cms.activities.duration').toLowerCase()}` : '');

      // Extract featured image from included data
      const mediaItems = extractMediaItems(item, included);
      
      // Look for featured media in the cms_activity_featured collection
      const featuredMedia = mediaItems.find((media: any) => {
        const collectionName = media?.attributes?.collection_name || media?.collection_name;
        return collectionName === 'cms_activity_featured';
      });
      
      let featuredImageUrl = '';
      if (featuredMedia) {
        const attributes = featuredMedia.attributes || featuredMedia;
        featuredImageUrl = attributes.original_url || attributes.storage_url || getMediaUrl(featuredMedia);
      }
      
      // If no featured image found, try to get any image from gallery as fallback
      if (!featuredImageUrl && mediaItems.length > 0) {
        const anyMedia = mediaItems[0];
        const attributes = anyMedia.attributes || anyMedia;
        featuredImageUrl = attributes.original_url || attributes.storage_url || getMediaUrl(anyMedia);
      }

      // Get travel type/modality
      const rawModality = (item?.attributes?.modality || item?.modality) || bsActivity?.modality || null;
      const modality = normalizeModalityKey(rawModality) || rawModality;

      // Get base price
      const basePrice = (item?.attributes?.base_price ?? item?.base_price) ?? item?.basePrice ?? null;

      // Get proper public status - check both attributes and direct property
      const isPublic = item?.isPublic;

      return {
        id: item?.id,
        title,
        summary,
        bsName,
        categoryKeys,
        categoryLabel: getCategoryLabel(categoryKeys),
        location,
        duration,
        availableLanguagesLabel: availableLanguages.map((lang: string) => lang?.toUpperCase()).join(', '),
        status: isPublic ? 'published' : 'draft',
        updatedAt: (item?.attributes?.updated_at || item?.updated_at) || item?.updatedAt || '',
        updatedAtLabel: formatDateTime((item?.attributes?.updated_at || item?.updated_at) || item?.updatedAt || ''),
        isPublic,
        isFeatured: !!(item?.attributes?.is_featured ?? item?.is_featured),
        featuredImage: featuredImageUrl ? {
          storageUrl: featuredImageUrl,
          originalUrl: featuredImageUrl,
        } : undefined,
        modality,
        base_price: basePrice,
      };
    };

    const loadActivities = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const filters: Record<string, any> = {
          order: { key: 'updated_at', direction: 'desc' },
        };

        if (selectedCategory.value) {
          filters.category = selectedCategory.value;
        }

        if (selectedStatus.value) {
          filters.status = selectedStatus.value;
        }

        if (selectedModality.value) {
          filters.modality = selectedModality.value;
        }

        if (selectedDifficulty.value) {
          filters.difficulty = selectedDifficulty.value;
        }

        if (searchQuery.value.trim()) {
          filters.search = searchQuery.value.trim();
        }

        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          ...filters,
        });

        const list = extractListData(response);
        const included = response?.included || [];
        activities.value = list.map((item: any) => mapActivity(item, included));
        // Extract pagination meta from response
        const responseMeta = response?.meta || response?.data?.meta;
        if (responseMeta) {
          paginationMeta.value = {
            currentPage: responseMeta.current_page || currentPage.value,
            lastPage: responseMeta.last_page || 1,
            perPage: responseMeta.per_page || perPage.value,
            from: responseMeta.from || null,
            to: responseMeta.to || null,
            total: responseMeta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load CMS activities', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    // Since filtering is now done server-side, filteredActivities just returns activities   
    const filteredActivities = computed(() => activities.value);

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadActivities();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1; // Reset to first page when changing per page
      loadActivities();
    };

    const navigateToCreate = () => {
      router.push({ name: 'cms.activities.create' });
    };

    const navigateToEdit = (activityId: number) => {
      router.push({ name: 'cms.activities.edit', params: { id: activityId } });
    };

    const handleDelete = async (activityId: number) => {
      if (!agencySlug.value) {
        return;
      }

      const activity = activities.value.find((a) => a.id === activityId);
      if (!activity) return;

      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = activityId;

      try {
        await api.activityService.deleteCmsActivity(agencySlug.value, activityId);
        activities.value = activities.value.filter((item) => item.id !== activityId);
        toast.success(t('cms.activities.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete CMS activity', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    const handleTogglePublish = async (activityId: number) => {
      if (!agencySlug.value) {
        return;
      }

      const activity = activities.value.find((a) => a.id === activityId);
      if (!activity) return;

      const nextState = !activity.isPublic;
      publishingId.value = activityId;

      try {
        await api.activityService.publishCmsActivity(agencySlug.value, activityId, nextState);
        activities.value = activities.value.map((item) =>
          item.id === activityId
            ? {
                ...item,
                isPublic: nextState,
                status: nextState ? 'published' : 'draft',
              }
            : item,
        );

        toast.success(
          nextState ? t('cms.activities.publishSuccess') : t('cms.activities.unpublishSuccess'),
        );
      } catch (error: any) {
        console.error('Failed to update publish state', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        publishingId.value = null;
      }
    };

    const deleteActivity = async (activity: CmsActivityCard) => {
      await handleDelete(activity.id);
    };

    const togglePublish = async (activity: CmsActivityCard) => {
      await handleTogglePublish(activity.id);
    };

    onMounted(() => {
      if (agencySlug.value) {
        loadActivities();
      }
    });

    watch(agencySlug, (slug) => {
      if (slug) {
        currentPage.value = 1;
        loadActivities();
      }
    });

    watch([selectedCategory, selectedStatus, selectedModality, selectedDifficulty], () => {
      if (agencySlug.value) {
        currentPage.value = 1;
        loadActivities();
      }
    });

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(searchQuery, () => {
      if (!agencySlug.value) return;
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
      searchDebounce = setTimeout(() => {
        currentPage.value = 1;
        loadActivities();
      }, 400);
    });

    return {
      categories,
      statusOptions,
      modalityOptions,
      getModalityLabel,
      getModalityClass,
      difficultyOptions,
      selectedCategory,
      selectedStatus,
      selectedModality,
      selectedDifficulty,
      searchQuery,
      filteredActivities,
      navigateToCreate,
      navigateToEdit,
      handleDelete,
      handleTogglePublish,
      deleteActivity,
      togglePublish,
      loadActivities,
      isLoading,
      errorMessage,
      publishingId,
      deletingId,
      paginationMeta,
      handlePageChange,
      handlePerPageChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-activities {
  color: $cms-text-primary;
}

.cms-activity-card {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-sm;
}

.cms-activity-preview {
  width: 100%;
  height: 150px;
  border-radius: $cms-radius-md;
  background-size: cover;
  background-position: center;
}

.cms-activity-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $cms-spacing-sm;
}

.cms-activity-title {
  font-size: $cms-font-size-lg;
  font-weight: 600;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.cms-activity-badges {
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-xs;
  flex-shrink: 0;
}

.cms-activity-modality {
  padding: $cms-spacing-xxs $cms-spacing-sm;
  border-radius: 20px;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  border: 1px solid transparent;
  letter-spacing: 0.02em;

  &--self-guided {
    background: $cms-info-light;
    color: #1565c0;
    border-color: rgba($cms-info-color, 0.35);
  }

  &--group {
    background: rgba(238, 42, 123, 0.12);
    color: #ad1457;
    border-color: rgba(238, 42, 123, 0.35);
  }
}

.cms-activity-featured {
  padding: $cms-spacing-xxs $cms-spacing-xs;
  background: $cms-warning-light;
  color: $cms-warning-color;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  text-transform: uppercase;
}

.cms-card-footer {
  display: flex;
  justify-content: flex-end;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-activities-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
  
  .loading-subtitle {
    font-size: 0.875rem;
    opacity: 0.7;
    margin: 0;
  }
}

.cms-empty-state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $cms-spacing-lg;
}

.empty-state-actions {
  display: flex;
  gap: $cms-spacing-md;
  justify-content: center;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.cms-activities-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}


.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 